import { Component,  ViewEncapsulation } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { LookupType, ShipmentCreationTemplateTypes } from '@shared-features/enums';
import { CustomerAPI } from '@features/customers/services/customer-api';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { modelToLookup } from '@shared/utils/model-to-lookup.util';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { of } from 'rxjs';
import { Lookup } from '@shared-features/models/Lookup.model';
import { MatDialogRef } from '@angular/material/dialog';
import { ShipmentTemplateService } from '@features/shipment-creation-template/services/shipment-template.service';
import { Router } from '@angular/router';
import { Customer } from '@features/customers/models/customer.model';
import { ShipmentCreationTemplate } from '@features/shipments/models/shipment-template.model';

@Component({
  selector: 'app-pre-create-shipment-dialog',
  template: ` <form [formGroup]="form">
    <app-dialog-layout>
      <ng-container dialog-title>
        <app-icon class="custom-dialog__icon" name="bold-user"></app-icon>
      </ng-container>

      <h5 class="text-900 mt-3 mb-0">{{ 'layout.PreCreateDialog.Title' | translate }}</h5>
      <p class="small-dialog mt-0">{{ 'layout.PreCreateDialog.Desc' | translate }}</p>
      <div *ngIf="form.get('customer') as customer">
        <mat-label>{{ 'layout.PreCreateDialog.MyCustomer' | translate }}</mat-label>
        <app-lazy-dropdown
          [lookup]="getCustomers"
          formControlName="customer"
          valuePath=""
          displayPath="title"
          [Model]="CustomerLookupModel"
        >
          <mat-error *ngIf="customer.touched && customer.invalid">Required</mat-error>
        </app-lazy-dropdown>
      </div>

      <div *ngIf="form.get('template') as template">
        <mat-label>{{ 'layout.PreCreateDialog.Template' | translate }}</mat-label>
        <app-lazy-dropdown [lookup]="templates$ | async" formControlName="template">
          <mat-error *ngIf="template.touched && template.invalid">Required</mat-error>
        </app-lazy-dropdown>
      </div>

      <div dialog-action class="w-100">
        <app-row gutter="x-xs">
          <app-col>
            <button type="button" mat-stroked-button class="d-block bg-white text-primary w-100" mat-dialog-close>
              {{ 'shared.Buttons.Cancel' | translate }}
            </button>
          </app-col>
          <app-col>
            <button type="button" mat-flat-button color="primary" class="d-block w-100" (click)="navigateToTemplate()">
              {{ 'layout.PreCreateDialog.Continue' | translate }}
            </button>
          </app-col>
        </app-row>
      </div>
    </app-dialog-layout>
  </form>`,
  styles: [
    `
      .small-dialog {
        max-width: 90vw;
        width: 420px;
      }

      .custom-dialog__icon {
        width: 3rem;
        height: 3rem;
        display: flex;
        position: relative;
        background-color: var(--color-primary-light);
        color: var(--color-primary);
        border: 0.5rem solid rgba(var(--color-primary-lighter-rgb), 0.9);
        border-radius: 0.5rem;
      }

      .custom-dialog__icon svg {
        margin: auto;
      }
    `,
  ],
  host: {
    class: 'small-dialog',
  },
  encapsulation: ViewEncapsulation.None,
})
export class PreCreateShipmentDialogComponent extends BaseComponent {
  customerLookup = LookupType.Customers;
  form = this.formBuilder.group({
    customer: [null, [Validators.required]],
    template: [null, [Validators.required]],
  });
  protected CustomerLookupModel = Customer;
  constructor(
    private formBuilder: FormBuilder,
    private customersApiService: CustomerAPI,
    private shipmentTemplateService: ShipmentTemplateService,
    private activeDialog: MatDialogRef<any>,
    private router: Router,

  ) {
    super();
  }

  getCustomers = (options: any) =>
    this.customersApiService
      .getAllCustomersWithPagination(options.offset, options.limit, [], options.searchTerm || '')
      .pipe(map((res) => res.customers));

  templates$ = this.form.controls.customer.valueChanges.pipe(
    switchMap((customer: Customer) => (customer?.id ? this.customersApiService.getCustomerTemplate(customer.id) : of(new Customer()))),
    map((customer) => customer.creationTemplates || []),
    map((templates) =>
      templates.map((t) =>
        Object.assign(new Lookup(), { id: t.type, value: this.translate(`Enum.ShipmentCreationTemplateTypes.${t.type}`) })
      )
    )
  );

  navigateToTemplate() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { template, customer } = this.form.value as any;
    const url = this.shipmentTemplateService.getCreateUrl(template?.id as any, {
      customerId: customer?.id,
    });
    this.router.navigateByUrl(url);
    this.activeDialog.close();
  }
}
